import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flame, ChevronRight, Plus, TrendingUp, TrendingDown, Minus, Calendar, Share2 } from 'lucide-react';
import { createShareCard, copyShareLink } from '@/lib/share';

const MOODS = [
  { key: 'happy', emoji: '😊', label: 'Happy', color: '#10b981' },
  { key: 'neutral', emoji: '😐', label: 'Neutral', color: '#a0a8b8' },
  { key: 'stressed', emoji: '😟', label: 'Stressed', color: '#f59e0b' },
  { key: 'angry', emoji: '😠', label: 'Angry', color: '#f97316' },
  { key: 'sad', emoji: '😢', label: 'Sad', color: '#ef4444' },
];

export default function BettingStreakTracker() {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [startBR, setStartBR] = useState('');
  const [endBR, setEndBR] = useState('');
  const [gameType, setGameType] = useState('Slots');
  const [casinoName, setCasinoName] = useState('');
  const [duration, setDuration] = useState('60');
  const [mood, setMood] = useState('neutral');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sessRes, statRes] = await Promise.all([api.get('/tools/sessions?limit=30'), api.get('/tools/sessions/stats')]);
      setSessions(sessRes.data.sessions);
      setStats(statRes.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tools/sessions', { starting_bankroll: parseFloat(startBR), ending_bankroll: parseFloat(endBR), game_type: gameType, casino_name: casinoName, duration_minutes: parseInt(duration), mood, notes });
      toast.success('Session logged!');
      setShowAdd(false);
      setStartBR(''); setEndBR(''); setNotes('');
      fetchData();
    } catch { toast.error('Failed to log session'); }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="streak-tracker-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Viral Tools</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Streak Tracker</span>
      </nav>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#f59e0b]/15 flex items-center justify-center shrink-0"><Flame className="h-6 w-6 text-[#f59e0b]" /></div>
          <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Betting Streak Tracker</h1><p className="text-[#a0a8b8] text-sm mt-1">Track wins, losses, and discover your patterns</p></div>
        </div>
        <div className="flex items-center gap-3">
          {stats && stats.total_sessions > 0 && (
            <button onClick={async () => {
              const card = await createShareCard('streak', { total_sessions: stats.total_sessions, win_rate: stats.win_rate, total_pl: stats.total_pl });
              if (card) copyShareLink(card.share_url);
            }} className="h-10 px-4 rounded-xl border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 flex items-center gap-2" data-testid="streak-share-btn">
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          )}
          <button onClick={() => setShowAdd(true)} className="h-10 px-5 rounded-xl bg-[#4d7cfe] text-white text-sm font-medium btn-lift flex items-center gap-2" data-testid="add-session-btn"><Plus className="h-4 w-4" /> Log Session</button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && stats.total_sessions > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Current Streak</p>
            <p className={`text-2xl font-bold ${stats.current_streak_type === 'win' ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
              {stats.current_streak} {stats.current_streak_type === 'win' ? 'W' : 'L'}
            </p></div>
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Total P&L</p>
            <p className={`text-2xl font-bold ${stats.total_pl >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>${stats.total_pl.toLocaleString()}</p>
            <p className="text-[10px] text-[#64748b]">{stats.total_sessions} sessions</p></div>
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Win Rate</p>
            <p className="text-2xl font-bold text-white">{stats.win_rate}%</p>
            <p className="text-[10px] text-[#64748b]">{stats.wins}W / {stats.losses}L</p></div>
          <div className="ciq-card py-3"><p className="text-xs text-[#64748b]">Best / Worst</p>
            <p className="text-sm"><span className="text-[#10b981] font-bold">+${stats.best_session}</span> / <span className="text-[#ef4444] font-bold">-${Math.abs(stats.worst_session)}</span></p></div>
        </div>
      )}

      {/* By Game */}
      {stats?.by_game && Object.keys(stats.by_game).length > 0 && (
        <div className="ciq-card mb-6">
          <h3 className="text-white font-semibold text-sm mb-3">Performance by Game</h3>
          <div className="space-y-2">{Object.entries(stats.by_game).map(([game, data]) => (
            <div key={game} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#1a1f2e]">
              <span className="text-sm text-white">{game}</span>
              <div className="flex items-center gap-4 text-xs">
                <span className="text-[#64748b]">{data.sessions} sessions</span>
                <span className={data.total_pl >= 0 ? 'text-[#10b981] font-bold' : 'text-[#ef4444] font-bold'}>${data.total_pl.toLocaleString()}</span>
              </div>
            </div>
          ))}</div>
        </div>
      )}

      {/* Session List */}
      <div className="ciq-card">
        <h3 className="text-white font-semibold text-sm mb-3">Recent Sessions</h3>
        {sessions.length === 0 && <p className="text-[#64748b] text-sm text-center py-6">No sessions logged yet. Click "Log Session" to start tracking.</p>}
        <div className="space-y-2">
          {sessions.map((s, i) => {
            const pl = s.profit_loss;
            const m = MOODS.find(m => m.key === s.mood);
            return (
              <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#1a1f2e]" data-testid={`session-${i}`}>
                {pl > 0 ? <TrendingUp className="h-4 w-4 text-[#10b981]" /> : pl < 0 ? <TrendingDown className="h-4 w-4 text-[#ef4444]" /> : <Minus className="h-4 w-4 text-[#a0a8b8]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">{s.game_type}{s.casino_name ? ` at ${s.casino_name}` : ''}</p>
                  <p className="text-xs text-[#64748b]">{new Date(s.date).toLocaleDateString()} - {s.duration_minutes}min</p>
                </div>
                <span className="text-xs">{m?.emoji}</span>
                <span className={`text-sm font-bold ${pl > 0 ? 'text-[#10b981]' : pl < 0 ? 'text-[#ef4444]' : 'text-[#a0a8b8]'}`}>
                  {pl >= 0 ? '+' : ''}${pl.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Session Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#252b3b] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Log Session</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#a0a8b8] mb-1 block">Starting $</label>
                <input type="number" value={startBR} onChange={e => setStartBR(e.target.value)} required
                  className="w-full h-10 px-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="session-start-input" /></div>
              <div><label className="text-xs text-[#a0a8b8] mb-1 block">Ending $</label>
                <input type="number" value={endBR} onChange={e => setEndBR(e.target.value)} required
                  className="w-full h-10 px-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="session-end-input" /></div>
            </div>
            <Select value={gameType} onValueChange={setGameType}>
              <SelectTrigger className="h-10 bg-[#1a1f2e] border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#252b3b] border-white/10">{['Slots', 'Blackjack', 'Roulette', 'Baccarat', 'Video Poker', 'Other'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
            <input type="text" value={casinoName} onChange={e => setCasinoName(e.target.value)} placeholder="Casino name (optional)"
              className="w-full h-10 px-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" />
            <div><label className="text-xs text-[#a0a8b8] mb-1.5 block">How did you feel?</label>
              <div className="flex gap-2">{MOODS.map(m => (
                <button key={m.key} type="button" onClick={() => setMood(m.key)}
                  className={`flex-1 h-10 rounded-lg text-center text-lg transition-all ${mood === m.key ? 'bg-white/10 ring-1 ring-white/20 scale-110' : 'bg-[#1a1f2e]'}`}>{m.emoji}</button>
              ))}</div>
            </div>
            <button type="submit" className="w-full h-11 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift" data-testid="session-save-btn">Save Session</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
